<template>
  <a-table
    v-if="!loading"
    :rowKey="record => record.WebhookId"
    :columns="googleColumns"
    :dataSource="data"
    :pagination="false"
    :loading="loading"
    :rowSelection="rowSelection"
    :class="{ 'safari-fix': isSafari }"
  >
    <template slot="google-formId" slot-scope="formId, record">
      <router-link
        v-if="record.WebhookName != null && record.WebhookName != ''"
        :to="'/edit-google-form/' + record.WebhookId"
      >
        {{record.WebhookName}}
        <span class="form-id">{{formId}}</span>
      </router-link>
      <span v-else-if="formId != null">
        Not yet activated
        <span class="form-id">{{record.WebhookId}}</span>
      </span>
      <span v-else>
        Waiting for test data
        <span class="form-id">Webhook ID: {{record.WebhookId}}</span>
      </span>
    </template>

    <template
      slot="google-last-request"
      slot-scope="lastRequest, record"
      v-if="record.WebhookId != null"
    >
      <span v-if="lastRequest == '1970-01-01T00:00:00.000Z'">None</span>
      <router-link
        :to="{path: `/view-logs/${record.WebhookId}`}"
        v-else
        :form-name="record.WebhookId"
      >
        {{formatDate(lastRequest)}}
        <a-icon type="bars" />
      </router-link>
    </template>

    <template slot="google-production-status" slot-scope="isProduction, record">
      <span v-if="isProduction == null">Pending</span>
      <a-switch v-else :defaultChecked="isProduction" @change="isProductionChanged(record, $event)"></a-switch>
    </template>

    <template slot="google-mapping-status" slot-scope="status, record">
      <span v-if="status == 'pending'">Pending</span>
      <a-switch
        v-else
        :defaultChecked="status == 'active'"
        @change="checkboxChanged(record, $event)"
      ></a-switch>
    </template>
  </a-table>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";

const googleColumns = [
  {
    title: "Form",
    dataIndex: "WebhookId",
    // sorter: sortFields,
    scopedSlots: { customRender: "google-formId" }
  },
  {
    title: "Last Request",
    dataIndex: "LastRequest",
    className: "centered-col",
    scopedSlots: { customRender: "google-last-request" }
  },
  {
    title: "Production",
    dataIndex: "IsProduction",
    className: "centered-col",
    scopedSlots: { customRender: "google-production-status" }
  },
  {
    title: "Active",
    dataIndex: "MappingStatus",
    className: "centered-col",
    scopedSlots: { customRender: "google-mapping-status" }
  },
  {
    title: "Created By",
    dataIndex: "CreatedByEmail"
  }
];

export default {
  data() {
    return {
      googleColumns,
      data: []
    };
  },
  computed: {
    ...mapState(["user", "apiUrl"]),
    rowSelection() {
      return {
        onChange: (selectedRowKeys, selectedRows) => {
          this.setSelectedRows(selectedRows);
        },
        getCheckboxProps: record => ({
          props: {
            disabled: record.WebhookId == null, // Column configuration not to be checked
            class: "tableSelectCheckboxOverride" // not working!!!
          }
        })
      };
    },
    // this little hack is to deal with position sticky elements not working
    // consistently across browsers that support it (I'm looking at you safari)
    isSafari() {
      return (
        navigator.vendor &&
        navigator.vendor.toLowerCase().indexOf("apple computer") > -1
      );
    }
  },
  methods: {
    // this returns back a function that is called when the checkbox changes.
    // this ensures the correct record is known
    checkboxChanged(rec, val) {
      axios({
        url: `${this.apiUrl}/manage-webhook`,
        method: "post",
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken
        },
        params: {
          action: "SET_MAPPING_STATUS",
          id: rec.WebhookId,
          formStatus: val ? "active" : "inactive"
        }
      });
    },
    isProductionChanged(rec, val) {
      axios({
        url: `${this.apiUrl}/manage-webhook`,
        method: "post",
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken
        },
        params: {
          action: "SET_PRODUCTION_STATUS",
          id: rec.WebhookId,
          prodStatus: val
        }
      });
    },
    formatDate(d) {
      if (d == "1970-01-01T00:00:00.000Z") {
        return "None";
      }
      const date = new Date(d);
      return date.toLocaleString();
    },
    changeFormName(newName) {
      this.$store.commit("setFormName", newName);
    },
    async deleteForms() {
      const deleteMsg =
        this.selectedRows.length > 1
          ? "Are you sure you want to delete these mappings?"
          : "Are you sure you want to delete this mapping?";
      if (confirm(deleteMsg)) {
        this.setLoading(true);

        for (const row of this.selectedRows) {
          await axios({
            method: "post",
            url: `${this.apiUrl}/manage-webhook`,
            headers: {
              Authorization: this.user.signInUserSession.idToken.jwtToken
            },
            params: {
              id: row.WebhookId,
              action: "DELETE_WEBHOOK"
            }
          }).then(rslt => {
            this.loadForms();
            this.setSelectedRows([]);
          });
        }
      }
    },
    loadForms() {
      this.setLoading(true);
      this.data = [];
      axios({
        method: "get",
        url: `${this.apiUrl}/manage-webhook`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken
        },
        params: {
          action: "GET_WEBHOOKS",
          platform: "google"
        }
      })
        .then(rslt => {
          this.data.push(...rslt.data);
          this.data = this.data.sort((a, b) => {
            if (a.CreatedByEmail < b.CreatedByEmail) {
              return -1;
            }
            if (a.CreatedByEmail > b.CreatedByEmail) {
              return 1;
            }
            return 0;
          });
          this.setLoading(false);
        })
        .catch(err => {
          alert("Unable to load forms");
          alert(err);
          console.log(err);
          this.setLoading(false);
        });
    }
  },
  mounted() {
    this.loadForms();
  },
  props: ["selectedRows", "setSelectedRows", "loading", "setLoading"]
};
</script>

<style lang="scss" scoped>
.form-id {
  color: #aaa;
  margin-left: 10px;
}

::v-deep {
  table .ant-table-thead {
    position: relative;
    z-index: 2;
    > tr > th {
      background: #fff;
      position: sticky;
      top: 0;
      z-index: 2;

      &.centered-col {
        text-align: center;
      }
    }
  }
  .ant-table-tbody > tr > td.centered-col {
    text-align: center;
  }
  tr[data-row-key^="page-row-"] {
    > td {
      position: sticky;
      top: 54px;
      z-index: 2;
      // NOTE: this is an exceptional case that !important is used.
      // Only allowed because overriding ant's styling which is unfortunately
      // nested deep so this helps keep it much more readable/maintainable
      // on our end.
      background: #fafafa !important;
    }
    .ant-table-selection-column span {
      visibility: hidden;
    }
    h3 {
      margin-bottom: 0;
      // stupid trick to get the title over so it visually is different than the
      // titles of the form titles.
      position: relative;
      left: -53px;
    }
    &:hover > td {
      // NOTE: this is an exceptional case that !important is used.
      // Only allowed because overriding ant's styling which is unfortunately
      // nested deep so this helps keep it much more readable/maintainable
      // on our end.
      background: #fafafa !important;
    }
  }
  .safari-fix {
    tr[data-row-key^="page-row-"] {
      > td {
        top: 0;
      }
    }
  }
}
</style>